import { useState, useCallback, useRef, useEffect } from "react";
import { chatAPI } from "../services/api.js";
import { interruptedResponseId } from "../utils/connectivity.js";
import {
  applyStreamEvent,
  CONNECTION_LOST,
  createAssistantMessage,
  finalizeMessage,
  toHistory,
} from "./messageStream.js";

// Les événements du flux sont appliqués par lots, au plus un rendu par intervalle :
// le Markdown de la réponse n'est pas ré-analysé à chaque morceau de texte
const STREAM_FLUSH_MS = 50;

/**
 * @param {object|null} userContext
 * @param {object|null} selectedModel
 * @param {{ensureConversation?: ({modelId, id?, enCreation?}) => Promise<string|null>, effort?: string|null,
 *   onModelUnavailable?: () => void}} options
 *   ensureConversation : renvoie la conversation de l'historique à laquelle rattacher
 *   le tour (créée au besoin), ou null sans historique.
 *   onModelUnavailable : appelé quand le serveur répond MODEL_UNAVAILABLE (contrat v1.6),
 *   pour relire la disponibilité des modèles sans attendre.
 *   effort : niveau choisi dans le compositeur (sprint 6), envoyé à chaque tour ; null ou
 *   « auto » : champ omis, le serveur choisit. Un niveau forcé pour une question
 *   (sendMessage(texte, {effort, action})) est noté sur la question, enregistré avec elle
 *   dans l'historique (conversationCodec : ui.effortOverride) et repris par « Régénérer »,
 *   même après rechargement.
 */
export function useChat(userContext, selectedModel = null, { ensureConversation = null, effort = null, onModelUnavailable = null } = {}) {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tools, setTools] = useState([]);
  const [serverStatus, setServerStatus] = useState({
    connected: false,
    toolsCount: 0,
  });
  const abortControllerRef = useRef(null);
  const userContextRef = useRef(userContext);
  userContextRef.current = userContext;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const selectedModelRef = useRef(selectedModel);
  selectedModelRef.current = selectedModel;
  const ensureConversationRef = useRef(ensureConversation);
  ensureConversationRef.current = ensureConversation;
  const effortRef = useRef(effort);
  effortRef.current = effort;
  const onModelUnavailableRef = useRef(onModelUnavailable);
  onModelUnavailableRef.current = onModelUnavailable;

  useEffect(() => {
    loadTools();
    checkServerHealth();
  }, []);

  const checkServerHealth = async () => {
    try {
      const health = await chatAPI.checkHealth();
      setServerStatus({
        connected: health.mcpConnected,
        toolsCount: health.toolsAvailable,
      });
    } catch (error) {
      console.error("Failed to check server health:", error);
      setServerStatus({ connected: false, toolsCount: 0 });
    }
  };

  const loadTools = async () => {
    try {
      const data = await chatAPI.getTools();
      setTools(data.tools || []);
    } catch (error) {
      console.error("Failed to load tools:", error);
    }
  };

  // Met à jour un seul message (les autres gardent leur référence : React.memo évite leur rendu)
  const updateMessage = useCallback((id, update) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? update(m) : m)));
  }, []);

  // Identifiant unique d'un message de l'interface (aussi clé de l'historique)
  const newId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  // Identifiant proposé pour une conversation à créer (lot 1) ; sans crypto.randomUUID (contexte
  // non sécurisé), aucun : la création précède alors la réponse, comme avant
  const newConversationId = () => (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : null);

  /**
   * Lance un tour : `base` est la conversation à envoyer, terminée par la question.
   * Elle remplace l'affichage (régénérer et modifier tronquent la suite), puis la
   * réponse s'écrit au fil des événements SSE.
   */
  const runTurn = useCallback(
    async (base) => {
      // Une requête encore ouverte (cas limite) est annulée avant la nouvelle
      abortControllerRef.current?.abort();

      // Historique lu dans la liste fournie : runTurn ne dépend pas de `messages` et ne
      // change pas à chaque morceau de texte reçu (pas de rendu de ChatInput)
      const history = toHistory(base);

      // Le contexte entreprise est transmis au serveur, qui l'injecte lui-même dans les
      // appels d'outils : il n'apparaît jamais dans le prompt (contrat C0, §1.2)
      const ctx = userContextRef.current;
      const context = ctx?.entreprise_id
        ? {
            entreprise_id: ctx.entreprise_id,
            user_id: ctx.user_id ?? null,
            email: ctx.email ?? null,
            user_api_hash: ctx.user_api_hash ?? null,
          }
        : null;
      const model = selectedModelRef.current;
      // Niveau d'effort : forcé pour cette question (point de situation), sinon la préférence
      const question = base[base.length - 1];
      const forced = question?.role === "user" && question.effortOverride ? question.effortOverride : null;
      const turnEffort = forced ?? effortRef.current;
      // Niveau fixé par une action rapide : noté sur le niveau appliqué (info-bulle « Niveau
      // fixé par l'action … » au lieu de « choisi dans le compositeur »)
      const withAction = (type, data) => {
        if (!forced || !question.effortAction || (type !== "start" && type !== "done")) return data;
        const effort = data?.effort;
        if (!effort || effort.source !== "utilisateur" || effort.niveau !== forced) return data;
        return { ...data, effort: { ...effort, action: question.effortAction } };
      };

      // Créer le message assistant vide qui sera rempli au fil des événements
      const streamingId = newId('assistant');
      const next = [...base, createAssistantMessage({ id: streamingId, selectedModel: model })];
      messagesRef.current = next;
      setMessages(next);
      setIsLoading(true);
      setError(null);

      const controller = new AbortController();
      abortControllerRef.current = controller;
      // `done`, `error` ou `end` reçu : la réponse est close côté interface
      let finished = false;

      // Rend la main à l'interface, sauf si une requête plus récente est déjà en cours
      const release = () => {
        if (abortControllerRef.current === controller) abortControllerRef.current = null;
        else if (abortControllerRef.current) return;
        setIsLoading(false);
      };

      const pending = [];
      let flushTimer = null;
      const flush = () => {
        if (flushTimer !== null) {
          clearTimeout(flushTimer);
          flushTimer = null;
        }
        if (!pending.length) return;
        const batch = pending.splice(0);
        updateMessage(streamingId, (m) =>
          batch.reduce((acc, [type, data, at]) => applyStreamEvent(acc, type, data, at), m)
        );
      };

      try {
        // Conversation de l'historique (créée au premier envoi) : la réponse y est
        // rattachée côté serveur. Sans historique, ou en cas d'échec, le chat continue.
        // Lot 1 (vitesse) : une conversation à créer l'est EN MÊME TEMPS que la réponse part
        // (identifiant proposé ici, enCreation appelé pendant l'appel, rattachement par le
        // serveur) ; la logique d'époque de useConversations est inchangée.
        let conversationId = null;
        let conversationEnCreation = null;
        if (ensureConversationRef.current) {
          try {
            const ensured = ensureConversationRef.current({
              modelId: model?.id ?? null,
              id: newConversationId(),
              enCreation: (id) => { conversationEnCreation = id; },
            });
            if (conversationEnCreation) {
              // Échec de la création : la réponse continue sans historique, comme avant
              Promise.resolve(ensured).catch(() => {});
            } else {
              conversationId = await ensured;
            }
          } catch {
            conversationId = null;
          }
        }

        await chatAPI.streamMessage(
          history,
          context,
          controller.signal,
          (type, data) => {
            pending.push([type, withAction(type, data), Date.now()]);
            // Modèle injoignable : la liste des modèles est relue (badge « Indisponible »)
            if (type === "error" && data?.code === "MODEL_UNAVAILABLE") onModelUnavailableRef.current?.();
            if (type === "done" || type === "error" || type === "end") {
              flush();
              if (!finished) {
                finished = true;
                release();
              }
            } else if (flushTimer === null) {
              flushTimer = setTimeout(flush, STREAM_FLUSH_MS);
            }
          },
          model?.id ?? null,
          conversationId,
          { effort: turnEffort, conversationEnCreation }
        );
        flush();

        // Flux terminé sans `done`, `error` ni `end` (connexion coupée par un proxy,
        // redémarrage du serveur) : la réponse est incomplète
        if (!finished) {
          updateMessage(streamingId, (m) =>
            m.isStreaming ? finalizeMessage({ ...m, error: m.error ?? CONNECTION_LOST }, "error") : m
          );
          release();
        }
      } catch (err) {
        flush();
        // Réponse déjà close : une coupure ensuite (avant `end`) ne la modifie pas
        if (finished) return;
        if (err.name !== "AbortError") {
          updateMessage(streamingId, (m) =>
            finalizeMessage({
              ...m,
              // Coupure après le début de la réponse : connexion interrompue ;
              // avant (fetch lève un TypeError) : serveur injoignable
              error: m.serverMessageId || m.content
                ? CONNECTION_LOST
                : {
                    code: "NETWORK",
                    message: err instanceof TypeError
                      ? "Connexion au serveur impossible. Vérifiez votre réseau puis réessayez."
                      : err.message,
                  },
            }, "error")
          );
        } else {
          // Annulation volontaire : garder le texte partiel
          updateMessage(streamingId, (m) => (m.isStreaming ? finalizeMessage(m, "stopped") : m));
        }
        release();
      }
    },
    [updateMessage]
  );

  const sendMessage = useCallback(
    (content, { effort: effortOverride = null, action = null } = {}) => {
      if (!content.trim()) return;
      const userMessage = {
        id: newId("user"),
        role: "user",
        content: content.trim(),
        ...(effortOverride ? { effortOverride, ...(action ? { effortAction: action } : {}) } : {}),
      };
      return runTurn([...messagesRef.current, userMessage]);
    },
    [runTurn]
  );

  /**
   * Régénérer (ou réessayer après une erreur / un arrêt) : renvoie la question qui
   * précède cette réponse ; la réponse et tout ce qui la suit sont remplacés.
   */
  const regenerate = useCallback(
    (assistantId) => {
      const list = messagesRef.current;
      const index = list.findIndex((m) => m.id === assistantId);
      if (index === -1) return;
      let userIndex = index - 1;
      while (userIndex >= 0 && list[userIndex].role !== "user") userIndex -= 1;
      if (userIndex < 0) return;
      return runTurn(list.slice(0, userIndex + 1));
    },
    [runTurn]
  );

  /**
   * Retour du réseau (UI-11) : relance la réponse interrompue par la coupure (dernier
   * message, erreur NETWORK), sans doublon : la question est renvoyée et la réponse
   * interrompue remplacée. Lu à l'instant de l'appel : un second clic, alors que la
   * nouvelle réponse a déjà commencé, ne relance rien.
   */
  const retryInterrupted = useCallback(() => {
    const id = interruptedResponseId(messagesRef.current);
    return id ? regenerate(id) : undefined;
  }, [regenerate]);

  /** Modifier une question : la conversation repart de ce point avec le nouveau texte. */
  const editMessage = useCallback(
    (userId, content) => {
      const text = content.trim();
      if (!text) return;
      const list = messagesRef.current;
      const index = list.findIndex((m) => m.id === userId && m.role === "user");
      if (index === -1) return;
      return runTurn([...list.slice(0, index), { id: newId("user"), role: "user", content: text }]);
    },
    [runTurn]
  );

  /** Affiche une conversation de l'historique (aucune requête en cours). */
  const loadMessages = useCallback((list) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsLoading(false);
    setError(null);
    messagesRef.current = list;
    setMessages(list);
  }, []);

  /**
   * Instantané de la conversation, réponse en cours comprise (close comme « arrêtée ») :
   * sauvegardé avant de quitter la conversation pendant une réponse.
   */
  const getSnapshot = useCallback(
    () => messagesRef.current.map((m) => (m.isStreaming ? finalizeMessage(m, "stopped") : m)),
    []
  );

  // Avis 👍 / 👎 (contrat C0, §3). Un nouveau clic sur le même pouce annule l'avis.
  const rateMessage = useCallback(async (id, rating, { reason = null, comment = "" } = {}) => {
    const target = messagesRef.current.find((m) => m.id === id);
    if (!target?.serverMessageId) throw new Error("Message non enregistré");

    const previous = target.feedback;
    const context = userContextRef.current?.entreprise_id ? userContextRef.current : null;
    const isUndo = rating === null;

    // Mise à jour optimiste, annulée en cas d'échec
    updateMessage(id, (m) => ({
      ...m,
      feedback: { rating, reason, comment, status: "sending" },
    }));

    try {
      if (isUndo) {
        await chatAPI.deleteFeedback(target.serverMessageId, context);
      } else {
        await chatAPI.sendFeedback({
          message_id: target.serverMessageId,
          rating,
          ...(reason ? { reason } : {}),
          ...(comment ? { comment: comment.slice(0, 500) } : {}),
          ...(context ? { context } : {}),
        });
      }
      updateMessage(id, (m) => ({ ...m, feedback: { ...m.feedback, status: "saved" } }));
    } catch (err) {
      updateMessage(id, (m) => ({ ...m, feedback: { ...previous, status: "idle" } }));
      throw err;
    }
  }, [updateMessage]);

  /**
   * Message produit par l'interface, hors flux du modèle (rapport composé, DOC-17) :
   * ajouté en fin de conversation, puis mis à jour par son identifiant.
   */
  const appendLocalMessage = useCallback((message) => {
    // Mise à jour fonctionnelle : une réponse en cours de streaming n'est pas écrasée
    setMessages((prev) => {
      const next = [...prev, message];
      messagesRef.current = next;
      return next;
    });
  }, []);

  const clearChat = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsLoading(false);
    messagesRef.current = [];
    setMessages([]);
    setError(null);
  }, []);

  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  }, []);

  return {
    messages,
    isLoading,
    error,
    tools,
    serverStatus,
    sendMessage,
    regenerate,
    retryInterrupted,
    editMessage,
    loadMessages,
    getSnapshot,
    rateMessage,
    appendLocalMessage,
    updateLocalMessage: updateMessage,
    clearChat,
    stopGeneration,
    checkServerHealth,
  };
}
